// Renters-Insurance_v03-theme.ts
// Readable theme tokens built on Bootstrap CSS vars.
// Override via CSS (preferred) or applyRentersTheme() at runtime.

export const RENTERS_THEME = {
  colors: {
    /** Brand & status */
    brandPrimaryColor: "var(--renters-primary, var(--bs-primary))",
    successColor: "var(--renters-success, var(--bs-success))",
    textOnBrandColor: "var(--renters-on-primary, #fff)",

    /** Page / global surface */
    pageBackgroundColor: "var(--renters-form-surface-bg, var(--bs-tertiary-color))",

    /** Cards (form panels) */
    cardBackgroundColorDefault: "var(--renters-card-bg, var(--bs-secondary-bg))",
    cardBackgroundColorSuccess:
      "var(--renters-success-bg-soft, var(--bs-success-bg-subtle, #d1e7dd))",

    /** Card borders */
    cardBorderColorDefault: "var(--renters-card-border, var(--bs-border-color))",
    cardBorderColorActive:
      "var(--renters-card-border-active, var(--renters-primary, var(--bs-primary)))",
    cardBorderColorSuccess: "var(--renters-success, var(--bs-success))",

    /** Card headers */
    cardHeaderBackgroundColorDefault:
      "var(--renters-card-header-default-bg, var(--bs-dark-border-subtle))",
    cardHeaderTextColorDefault:
      "var(--renters-card-header-default-text, var(--bs-body-color))",

    cardHeaderBackgroundColorActive:
      "var(--renters-card-header-active-bg, var(--renters-primary, var(--bs-primary)))",
    cardHeaderTextColorActive:
      "var(--renters-card-header-active-text, var(--renters-on-primary, #fff))",

    /** Sticky summary header (vertical mode) */
    stickyHeaderBackgroundColor:
      "var(--renters-sticky-header-bg, var(--bs-body-bg))",

    /** Soft badges */
    badgeBackgroundColorSoft: "var(--renters-badge-bg, var(--bs-success-bg-subtle))",
    badgeTextColorSoft: "var(--renters-badge-text, var(--bs-success-bg-subtle))",
  },

  layout: {
    /** Fixed width so the language switch never jiggles */
    langSwitchWidthPx: 56,
    stickyTopPx: 16,
    progressMinWidthPx: 160,
    progressMinWidthVerticalPx: 180,
    verticalScrollMarginTopPx: 90,
  },
} as const;

export type RentersTheme = typeof RENTERS_THEME;

/**
 * Optional: programmatic overrides at runtime.
 * Example:
 *   applyRentersTheme({
 *     pageBackgroundColor: "var(--bs-gray-100)",
 *     brandPrimaryColor: "#0ea5a5",
 *     cardHeaderBackgroundColorActive: "#0ea5a5",
 *   })
 */
export function applyRentersTheme(overrides: Partial<{
  // Brand & status
  brandPrimaryColor: string;
  successColor: string;
  textOnBrandColor: string;

  // Page / global
  pageBackgroundColor: string;

  // Cards
  cardBackgroundColorDefault: string;
  cardBackgroundColorSuccess: string;

  // Borders
  cardBorderColorDefault: string;
  cardBorderColorActive: string;
  cardBorderColorSuccess: string;

  // Headers
  cardHeaderBackgroundColorDefault: string;
  cardHeaderTextColorDefault: string;
  cardHeaderBackgroundColorActive: string;
  cardHeaderTextColorActive: string;

  // Sticky / badges
  stickyHeaderBackgroundColor: string;
  badgeBackgroundColorSoft: string;
  badgeTextColorSoft: string;

  // OPTIONAL: directly tweak Bootstrap app-wide vars
  bsBodyBg: string;
  bsCardBg: string;
  bsBorderColor: string;
}> = {}) {
  const root = document.documentElement;

  // Brand & status
  if (overrides.brandPrimaryColor) root.style.setProperty("--renters-primary", overrides.brandPrimaryColor);
  if (overrides.successColor) root.style.setProperty("--renters-success", overrides.successColor);
  if (overrides.textOnBrandColor) root.style.setProperty("--renters-on-primary", overrides.textOnBrandColor);

  // Page / global
  if (overrides.pageBackgroundColor) root.style.setProperty("--renters-form-surface-bg", overrides.pageBackgroundColor);

  // Cards
  if (overrides.cardBackgroundColorDefault) root.style.setProperty("--renters-card-bg", overrides.cardBackgroundColorDefault);
  if (overrides.cardBackgroundColorSuccess) root.style.setProperty("--renters-success-bg-soft", overrides.cardBackgroundColorSuccess);

  // Borders
  if (overrides.cardBorderColorDefault) root.style.setProperty("--renters-card-border", overrides.cardBorderColorDefault);
  if (overrides.cardBorderColorActive) root.style.setProperty("--renters-card-border-active", overrides.cardBorderColorActive);
  if (overrides.cardBorderColorSuccess) root.style.setProperty("--renters-success", overrides.cardBorderColorSuccess);

  // Headers
  if (overrides.cardHeaderBackgroundColorDefault) root.style.setProperty("--renters-card-header-default-bg", overrides.cardHeaderBackgroundColorDefault);
  if (overrides.cardHeaderTextColorDefault) root.style.setProperty("--renters-card-header-default-text", overrides.cardHeaderTextColorDefault);
  if (overrides.cardHeaderBackgroundColorActive) root.style.setProperty("--renters-card-header-active-bg", overrides.cardHeaderBackgroundColorActive);
  if (overrides.cardHeaderTextColorActive) root.style.setProperty("--renters-card-header-active-text", overrides.cardHeaderTextColorActive);

  // Sticky / badges
  if (overrides.stickyHeaderBackgroundColor) root.style.setProperty("--renters-sticky-header-bg", overrides.stickyHeaderBackgroundColor);
  if (overrides.badgeBackgroundColorSoft) root.style.setProperty("--renters-badge-bg", overrides.badgeBackgroundColorSoft);
  if (overrides.badgeTextColorSoft) root.style.setProperty("--renters-badge-text", overrides.badgeTextColorSoft);

  // OPTIONAL: Bootstrap globals (broad impact)
  if (overrides.bsBodyBg) root.style.setProperty("--bs-body-bg", overrides.bsBodyBg);
  if (overrides.bsCardBg) root.style.setProperty("--bs-card-bg", overrides.bsCardBg);
  if (overrides.bsBorderColor) root.style.setProperty("--bs-border-color", overrides.bsBorderColor);
}
